/**
 * MailchimpStatusBadge — small badge on contact cards showing Mailchimp subscriber status.
 *
 * Hidden (returns null) when status is null, undefined, or 'not_synced' — this is the
 * fast path for contact cards in orgs without Mailchimp or for unsynced contacts.
 */

import { Badge } from '@/lib/base/badges/badges';
import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';

import type { MailchimpSubscriberStatus } from '../types';

interface MailchimpStatusBadgeProps {
  status: MailchimpSubscriberStatus | null | undefined;
  audienceId?: string;
  subscriberHash?: string;
  datacenter?: string;
}

const STATUS_DISPLAY: Record<
  Exclude<MailchimpSubscriberStatus, 'not_synced'>,
  {
    color: 'success' | 'warning' | 'brand' | 'gray' | 'gray-blue';
    label: string;
    tooltip: string;
  }
> = {
  subscribed: {
    color: 'success',
    label: 'Subscribed',
    tooltip: 'Active subscriber in Mailchimp',
  },
  unsubscribed: {
    color: 'gray',
    label: 'Unsubscribed',
    tooltip: 'Opted out of Mailchimp emails',
  },
  cleaned: {
    color: 'warning',
    label: 'Cleaned',
    tooltip: 'Bounced or marked invalid – Mailchimp removed from sending',
  },
  pending: {
    color: 'brand',
    label: 'Pending',
    tooltip: 'Awaiting opt-in confirmation',
  },
  transactional: {
    color: 'gray',
    label: 'Transactional',
    tooltip: 'Receives transactional emails only',
  },
  // TODO(copy): 'Archived' is a banned state word — mirrors Mailchimp's own vocabulary; naming decision needs Laura sign-off
  archived: {
    color: 'gray-blue',
    label: 'Archived',
    tooltip: 'Archived in Mailchimp (no longer sent emails)',
  },
};

export const MailchimpStatusBadge = ({
  status,
  audienceId,
  subscriberHash,
  datacenter,
}: MailchimpStatusBadgeProps) => {
  if (status == null || status === 'not_synced') {
    return null;
  }

  const display = STATUS_DISPLAY[status];

  if (audienceId && subscriberHash && datacenter) {
    return (
      <Tooltip title={display.tooltip}>
        <a
          href={`https://${datacenter}.admin.mailchimp.com/audience/contact-profile?contact_id=${subscriberHash}&list_id=${audienceId}`}
          target='_blank'
          rel='noopener noreferrer'
          className='inline-block'
        >
          <Badge color={display.color} size='sm' type='pill-color'>
            {display.label}
          </Badge>
        </a>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={display.tooltip}>
      <TooltipTrigger>
        <Badge color={display.color} size='sm' type='pill-color'>
          {display.label}
        </Badge>
      </TooltipTrigger>
    </Tooltip>
  );
};
